import { convertGamesToSports, convertGamesToTournaments } from "@finbackoffice/site-core";
import { IEventFeedResponse, ITopLeagueFeedResponse } from "@finbackoffice/websocket-client";
import { useRouter } from "next/router";
import { FC, useCallback, useMemo } from "react";
import { useSelectedPrematchGameId } from "hooks";
import SportsLayout from "components/layout/sportsbook/sports/SportsLayout";
import FadeInAnimation from "components/base/fade-in/FadeInAnimation";
import PrematchTournamentFlex from "../prematch/tournament/flex/PrematchTournamentFlex";
import styles from "./upcoming-live-tournaments.module.sass";

type IProps = {
    events: IEventFeedResponse[];
};

const PrematchSportsAndTournamentsContainer: FC<IProps> = ({ events }) => {
    const router = useRouter();
    const selectedGameId = useSelectedPrematchGameId();

    const sports = useMemo(() => convertGamesToSports(events), [events]);

    const activeSportId = useMemo(() => {
        const [sportSlug] = (router.query.prematchGameRoutes || []) as string[];
        return sports.find((sport) => sport.slug === sportSlug)?.id ?? sports[0]?.id;
    }, [router.query, sports]);

    const activeSportEvents = useMemo(
        () => events.filter((event) => event.sport_id === activeSportId),
        [activeSportId, events],
    );

    const activeSportTournaments = useMemo(
        () => convertGamesToTournaments(activeSportEvents),
        [activeSportEvents],
    );

    const getGamesByTournamentId = useCallback(
        (id: number) => activeSportEvents.filter((event) => event.tournament_id === id),
        [activeSportEvents],
    );

    const renderTournament = useCallback(
        (tournament: ITopLeagueFeedResponse, index: number) => {
            const games = getGamesByTournamentId(tournament.id);
            const containsSelectedGame = !!games.find((game) => game.id === selectedGameId);
            return (
                <PrematchTournamentFlex
                    key={tournament.id}
                    tournament={tournament}
                    games={games}
                    isOpenedByDefault={index < 5 || containsSelectedGame}
                    isFirst={index === 0}
                />
            );
        },
        [getGamesByTournamentId, selectedGameId],
    );

    return (
        <div>
            <SportsLayout sports={sports} activeSportId={activeSportId || undefined}>
                <FadeInAnimation>
                    <ul className={styles.topLeaguesList}>
                        {Boolean(activeSportTournaments?.length) &&
                            activeSportTournaments.map(renderTournament)}
                    </ul>
                </FadeInAnimation>
            </SportsLayout>
        </div>
    );
};

export default PrematchSportsAndTournamentsContainer;
